import { useState, useMemo } from "react";
import { Plane, Star, Trash2, Plus, RefreshCw, AlertTriangle } from "lucide-react";
import styles from "./DestinationAirports.module.css";
import BaseModal from "../../../components/BaseComponents/BaseModal/BaseModal";
import { useToast } from "../../../context/ToastContext";
import { useDestinationAirports, saveDestinationAirports, syncDestinationAirports } from "../../../api/geo/useDestinationAirports";

/*
 * GeoData → Destination airports (build order, step 11; Levent, 6 Oct 2026; Destination Airport
 * Mapping spec + answers, 7 Oct 2026).
 *
 * Every active GeoData destination is listed, grouped by country; the list is synced with GeoData
 * on every load (and on demand with "Sync from GeoData"), so a destination added or activated in
 * GeoData is never missed: until it has an airport it shows as "Airport mapping missing" and gets
 * no flight + hotel packages (Hotel Only is unaffected).
 *
 * Each destination has one MAIN airport plus up to three approved alternatives. Only these
 * airports are searched for its hotels' packages, and only for the hotels inside the airport's
 * GeoData scope: an airport linked to zones of the destination covers only those zones' hotels
 * (e.g. one island), linked without zones the whole destination, not linked at all no hotel
 * ("Approved airport is not linked in GeoData"). Covered Hotels is that count: unique hotels,
 * read-only, computed now.
 *
 * "Flight + Hotel" Enabled / Disabled switches a destination's packages off without removing its
 * airports; it is separate from the GeoData Active / Inactive state. An airport removed here is
 * deactivated, not deleted.
 *
 * "Flight Connection Policy" (Master Ch 1 §12B.6, formerly the "Long-haul" field, same stored
 * value): the ONLY routing rule for packages to this destination, per direction. DIRECT_ONLY =
 * nonstop flights only; DIRECT_OR_MAX_1_STOP = at most one stop or connection. The departure
 * airport's own policy is no longer applied, and no holiday-type rule is added on top.
 */

// The API's status, with the older list shape (no `status`) read the way it always was.
const STATUS_KEY = { APPROVED: "ok", NEEDS_REVIEW: "review", UNMAPPED: "none", DISABLED: "disabled", GEODATA_INACTIVE: "inactive" };
const statusOf = (d) => (d.status ? STATUS_KEY[d.status] || "none" : !d.airports.length ? "none" : d.needsReview ? "review" : "ok");
const STATUS_LABEL = { none: "Airport mapping missing", review: "Needs review", ok: "Approved", disabled: "Flight + Hotel disabled", inactive: "GeoData inactive" };
const WARNING_TEXT = {
  NOT_LINKED_IN_GEODATA: "Approved airport is not linked in GeoData.",
  LINKED_ZONES_INACTIVE: "Approved airport is linked only to zones that are no longer active in GeoData.",
};
const keyOf = (d) => d.destinationId ?? d.destinationCode;
const mainOf = (d) => d.mainAirport ?? d.airports.find((a) => a.isMain)?.airportCode ?? null;
const alternativesOf = (d) => d.alternativeAirports ?? d.airports.filter((a) => !a.isMain).map((a) => a.airportCode);
const coveredOf = (d) => d.coveredHotels ?? d.hotels;

const DestinationAirports = () => {
  const { showToast } = useToast();
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [country, setCountry] = useState("");
  const { data, maxAirports, loading, error, refetch } = useDestinationAirports({ search, needsReview: onlyOpen });
  const [editing, setEditing] = useState(null);      // { key, destinationCode, name, rows: [{airportCode, isMain}], longHaul, flightHotelEnabled }
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [formError, setFormError] = useState("");

  const countries = useMemo(() => [...new Set(data.map((d) => d.countryName).filter(Boolean))].sort(), [data]);
  const shown = useMemo(() => (country ? data.filter((d) => d.countryName === country) : data), [data, country]);
  const counts = useMemo(() => data.reduce((c, d) => ({ ...c, [statusOf(d)]: (c[statusOf(d)] || 0) + 1 }), {}), [data]);
  const coverageTitle = (d) => (d.airports.length ? d.airports.map((a) => `${a.airportCode}: ${a.coveredHotels ?? "?"}`).join(" · ") : "");

  const openEdit = (d) => {
    setFormError("");
    const rows = d.airports.length
      ? d.airports.map((a) => ({ airportCode: a.airportCode, isMain: a.isMain }))
      : d.linkedAirports.slice(0, maxAirports).map((code, i) => ({ airportCode: code, isMain: i === 0 }));
    setEditing({
      key: keyOf(d), destinationCode: d.destinationCode, name: d.name, linked: d.linkedAirports, rows,
      longHaul: !!d.longHaul, flightHotelEnabled: d.flightHotelEnabled !== false,
    });
  };

  const setRow = (i, patch) => setEditing((e) => ({ ...e, rows: e.rows.map((r, k) => (k === i ? { ...r, ...patch } : r)) }));
  const setMain = (i) => setEditing((e) => ({ ...e, rows: e.rows.map((r, k) => ({ ...r, isMain: k === i })) }));
  const removeRow = (i) => setEditing((e) => {
    const rows = e.rows.filter((_, k) => k !== i);
    if (rows.length && !rows.some((r) => r.isMain)) rows[0] = { ...rows[0], isMain: true };
    return { ...e, rows };
  });
  const addRow = () => setEditing((e) => ({ ...e, rows: [...e.rows, { airportCode: "", isMain: e.rows.length === 0 }] }));

  const save = async (key, label, rows, extra = {}) => {
    setSaving(true);
    setFormError("");
    try {
      await saveDestinationAirports(key, rows.map((r) => ({ airportCode: r.airportCode.trim().toUpperCase(), isMain: r.isMain })), extra);
      showToast(`Airports saved for ${label}`, "success");
      setEditing(null);
      refetch();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message;
      if (editing) setFormError(msg); else showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await syncDestinationAirports();
      showToast(r.added ? `${r.added} new destination${r.added === 1 ? "" : "s"} from GeoData` : "Up to date with GeoData", "success");
      refetch();
    } catch (e) {
      showToast(e?.response?.data?.message || e.message, "error");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.title}><Plane size={18} /> Destination airports</div>
        <div className={styles.summary}>
          {counts.ok || 0} approved · {counts.review || 0} need review · {counts.none || 0} airport mapping missing
          {counts.disabled ? ` · ${counts.disabled} disabled` : ""}{counts.inactive ? ` · ${counts.inactive} GeoData inactive` : ""}
        </div>
        <button className={styles.secondaryBtn} onClick={sync} disabled={syncing} title="Add the destinations newly activated in GeoData">
          <RefreshCw size={14} /> {syncing ? "Syncing…" : "Sync from GeoData"}
        </button>
      </div>

      <div className={styles.note}>
        Every active GeoData destination is listed; a new one appears as <b>Airport mapping missing</b> and gets no
        flight + hotel packages until an airport is approved. Per destination: one <b>main</b> airport and up to {maxAirports - 1} alternatives.
        An airport covers only the hotels inside its GeoData scope (its linked zones, or the whole destination when it is
        linked without zones); <b>Covered hotels</b> counts those, each hotel once. The cheapest complete package wins
        whichever airport it uses; the main airport only decides the default order. The <b>Flight Connection Policy</b> alone
        decides the routing, in each direction: <b>Direct only</b> (nonstop) or <b>Max 1 stop</b>. The departure airport adds no restriction.
      </div>

      <div className={styles.filterBar}>
        <form onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); }} className={styles.searchForm}>
          <input className={styles.input} placeholder="Search destination, zone, airport or country" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          <button type="submit" className={styles.secondaryBtn}>Search</button>
        </form>
        <select className={styles.input} value={country} onChange={(e) => setCountry(e.target.value)} aria-label="Country">
          <option value="">All countries</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className={styles.check}>
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> Only needs review / missing / warnings
        </label>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading ? <div className={styles.loading}>Loading…</div> : (
        <table className={styles.table}>
          <thead>
            <tr><th>Country</th><th>Destination</th><th>Main airport</th><th>Alternative airports</th><th>Covered hotels</th><th>Flight + Hotel</th><th>Flight Connection Policy</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {shown.map((d) => (
              <tr key={keyOf(d)}>
                <td>{d.countryName}</td>
                <td>
                  <b>{d.name}</b> <span className={styles.code}>{d.destinationCode}</span>
                  {d.matchedZones?.length > 0 && <div className={styles.muted}>Zone: {d.matchedZones.join(", ")}</div>}
                </td>
                <td>
                  {mainOf(d) ? <span className={styles.chipMain} title="Main airport"><Star size={11} /> {mainOf(d)}</span> : <span className={styles.muted}>—</span>}
                </td>
                <td>
                  {alternativesOf(d).length ? alternativesOf(d).map((code) => (
                    <span key={code} className={styles.chip} title="Alternative">{code}</span>
                  )) : <span className={styles.muted}>—</span>}
                  {(d.warnings || []).map((w) => (
                    <div key={`${w.airportCode}-${w.code}`} className={styles.warn}><AlertTriangle size={12} /> {w.airportCode}: {WARNING_TEXT[w.code] || w.code}</div>
                  ))}
                </td>
                {/* "covered / hotels in the destination": 0 / 0 is a destination without hotels,
                    0 / 85 an airport whose GeoData links do not reach them. */}
                <td title={coverageTitle(d)}>
                  <span>{coveredOf(d)}</span>
                  {d.hotels != null && <span className={styles.muted}> / {d.hotels}</span>}
                </td>
                <td>{d.flightHotelEnabled === false ? <span className={styles.muted}>Disabled</span> : "Enabled"}</td>
                <td>{d.longHaul ? <span className={styles.chipLongHaul}>Max 1 stop</span> : <span className={styles.muted}>Direct only</span>}</td>
                <td><span className={styles[`status_${statusOf(d)}`]}>{STATUS_LABEL[statusOf(d)]}</span></td>
                <td className={styles.actions}>
                  {statusOf(d) === "review" && (
                    <button className={styles.secondaryBtn} disabled={saving} onClick={() => save(keyOf(d), d.destinationCode, d.airports)}>Confirm</button>
                  )}
                  <button className={styles.primaryBtn} onClick={() => openEdit(d)}>Edit</button>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={9} className={styles.muted}>No destinations found.</td></tr>}
          </tbody>
        </table>
      )}

      <BaseModal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `Airports for ${editing.name} (${editing.destinationCode})` : ""}
        width="520px"
        footer={editing && (
          <div className={styles.modalFooter}>
            <button className={styles.secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
            <button className={styles.primaryBtn} disabled={saving} onClick={() => save(editing.key, editing.destinationCode, editing.rows, { longHaul: editing.longHaul, flightHotelEnabled: editing.flightHotelEnabled })}>{saving ? "Saving…" : "Save"}</button>
          </div>
        )}
      >
        {editing && (
          <div className={styles.form}>
            {editing.rows.map((r, i) => (
              <div key={i} className={styles.formRow}>
                <input className={styles.codeInput} maxLength={3} placeholder="IATA" value={r.airportCode} onChange={(e) => setRow(i, { airportCode: e.target.value.toUpperCase() })} />
                <label className={styles.check}>
                  <input type="radio" name="main" checked={r.isMain} onChange={() => setMain(i)} /> Main
                </label>
                <button className={styles.iconBtn} title="Remove" onClick={() => removeRow(i)}><Trash2 size={14} /></button>
              </div>
            ))}
            {editing.rows.length < maxAirports && (
              <button className={styles.secondaryBtn} onClick={addRow}><Plus size={14} /> Add airport</button>
            )}
            {!editing.rows.length && <div className={styles.warn}>Without an airport this destination gets no flight + hotel packages.</div>}
            {editing.linked?.length > 0 && <div className={styles.muted}>Linked in GeoData: {editing.linked.join(", ")}</div>}
            <label className={styles.check}>
              <input type="checkbox" checked={editing.flightHotelEnabled} onChange={(e) => setEditing((x) => ({ ...x, flightHotelEnabled: e.target.checked }))} />
              Flight + Hotel mapping enabled
            </label>
            <div className={styles.muted}>
              Disabled: no new flight + hotel packages for this destination; its airports stay stored and Hotel Only is unaffected.
            </div>
            <fieldset className={styles.policyChoice}>
              <legend>Flight Connection Policy</legend>
              <label className={styles.check}>
                <input type="radio" name="flightConnectionPolicy" checked={!editing.longHaul} onChange={() => setEditing((x) => ({ ...x, longHaul: false }))} />
                Direct flights only (DIRECT_ONLY)
              </label>
              <label className={styles.check}>
                <input type="radio" name="flightConnectionPolicy" checked={!!editing.longHaul} onChange={() => setEditing((x) => ({ ...x, longHaul: true }))} />
                Direct flights + maximum 1 stop per direction (DIRECT_OR_MAX_1_STOP)
              </label>
            </fieldset>
            <div className={styles.muted}>
              Applies to every approved airport of this destination, out and back. The departure airport adds no restriction (Master §12B.6).
            </div>
            {formError && <div className={styles.error}>{formError}</div>}
          </div>
        )}
      </BaseModal>
    </div>
  );
};

export default DestinationAirports;
